import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BranchesService } from '../../services/branches.service';
import { AuthService } from '../../services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { Branch, BranchCategory, BranchWithCategory } from '../../interfaces/branch';
import { MapComponent } from '../map/map.component';
import { CommonModule } from '@angular/common';
import { ResponseModalComponent } from '../response-modal/response-modal.component';
import { ButtonComponent } from '../button/button.component';
import { SkeletonComponent } from '../skeleton/skeleton.component';
import { ReservationSelectionCreate } from '../../interfaces/reservation-selection';
import { CartService } from '../../services/cart.service';
import { CartToastService } from '../../services/cart-toast.service';
import { TranslateModule } from '@ngx-translate/core';
import { TrackingService } from '../../services/tracking.service';

/** Selección confirmada: 1 sucursal + 1 categoría */
export interface HopSelection {
  branchId: number;
  categoryId: number;
  branchName: string;     // para display
  categoryName: string;   // para display
  price: number;          // precio de esa branchCategory
}

@Component({
  selector: 'app-new-hop',
  standalone: true,
  imports: [MapComponent, CommonModule, ButtonComponent, SkeletonComponent, TranslateModule],
  templateUrl: './new-hop.component.html',
  styleUrl: './new-hop.component.scss'
})
export class NewHopComponent implements OnInit {

  branches: Branch[] | null = null;
  branchesLoading = true;
  isAuthenticated = false;
  loading = false;
  minBreweries = 3;
  panelCollapsed = false;

  /** Selecciones confirmadas (branchId, categoryId únicos) */
  selections: HopSelection[] = [];

  /** IDs de branches seleccionados para el mapa */
  branchesForMap: number[] = [];

  // ── Modal de categorías ───────────────────────────────────────────
  modalOpen = false;
  modalBranch: Branch | null = null;

  constructor(
    private branchesService: BranchesService,
    private authService: AuthService,
    private router: Router,
    private dialog: MatDialog,
    private cartService: CartService,
    private cartToastService: CartToastService,
    private trackingService: TrackingService
  ) {}

  ngOnInit(): void {
    this.authService.getTokenChanges().subscribe(token => {
      this.isAuthenticated = !!token;
    });
    this.loadBranches();
  }

  // ── Carga de sucursales ───────────────────────────────────────────

  loadBranches(): void {
    this.branchesLoading = true;
    this.branchesService.getBranchesHopping().subscribe({
      next: branches => {
        this.branches = branches;
        this.branchesLoading = false;
        this.restoreSelections();
      },
      error: err => {
        console.error('Error loading branches:', err);
        this.router.navigate(['']);
        this.branchesLoading = false;
      }
    });
  }

  // ── Interacción con el mapa ───────────────────────────────────────

  /**
   * Toggle panel and invalidate map size after animation
   */
  togglePanel(): void {
    this.panelCollapsed = !this.panelCollapsed;
    // After CSS transition (300ms), tell the map to recalculate its size
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 350);
  }

  /**
   * Llamado por el mapa cuando el usuario hace click en un pin.
   * Si la sucursal ya está seleccionada abre el modal para ver/cambiar.
   * Si no está seleccionada abre el modal para elegir categoría.
   */
  toggleNewHop(branchId: number): void {
    const branch = this.branches?.find(b => b.id === branchId);
    if (!branch) return;
    this.openCategoryModal(branch);
  }

  // ── Modal de categorías ───────────────────────────────────────────

  openCategoryModal(branch: Branch): void {
    this.modalBranch = branch;
    this.modalOpen = true;
  }

  closeModal(): void {
    this.modalOpen = false;
    this.modalBranch = null;
  }

  /** Devuelve la selección actual de esa sucursal (si existe) */
  getSelectionForBranch(branchId: number): HopSelection | undefined {
    return this.selections.find(s => s.branchId === branchId);
  }

  /** Categorías disponibles del branch abierto en el modal */
  get modalCategories(): BranchCategory[] {
    return this.modalBranch?.branchCategories?.filter(bc => bc.available && parseFloat(bc.price) > 0) ?? [];
  }

  /**
   * Devuelve solo los productos de una BranchCategory que estén disponibles
   * en este branch (tienen un branchProduct con available = true).
   */
  getAvailableProducts(branchId: number, bc: BranchCategory): any[] {
    return (bc.category?.products ?? []).filter(p =>
      p.branchProducts?.some(bp => bp.branchId === branchId && bp.available)
    );
  }

  /** Selecciona una categoría en el modal y cierra */
  selectCategory(bc: BranchCategory): void {
    if (!this.modalBranch) return;

    const branchId = this.modalBranch.id;
    const branchName = `${this.modalBranch.brewery?.name} — ${this.modalBranch.address}`;
    const categoryName = bc.category?.name ?? `Categoría ${bc.categoryId}`;
    const price = parseFloat(bc.price);

    // Quitar selección previa de esta sucursal (si existía una categoría distinta)
    this.selections = this.selections.filter(s => s.branchId !== branchId);

    this.selections.push({ branchId, categoryId: bc.categoryId, branchName, categoryName, price });

    // Actualizar mapa
    if (!this.branchesForMap.includes(branchId)) {
      this.branchesForMap = [...this.branchesForMap, branchId];
    }

    this.saveSelections();
    this.closeModal();
  }

  /** Quita una sucursal del recorrido */
  removeSelection(branchId: number): void {
    this.selections = this.selections.filter(s => s.branchId !== branchId);
    this.branchesForMap = this.branchesForMap.filter(id => id !== branchId);
    this.saveSelections();
  }

  // ── Persistencia en localStorage ──────────────────────────────────

  private readonly STORAGE_KEY = 'hop_selections';

  private saveSelections(): void {
    try {
      const data = this.selections.map(s => ({
        branchId: s.branchId,
        categoryId: s.categoryId,
      }));
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (e) { /* ignore */ }
  }

  private restoreSelections(): void {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return;

      const saved: { branchId: number; categoryId: number }[] = JSON.parse(raw);
      if (!Array.isArray(saved) || saved.length === 0) return;

      const restored: HopSelection[] = [];

      for (const item of saved) {
        const branch = this.branches?.find(b => b.id === item.branchId);
        if (!branch) continue;

        const bc = branch.branchCategories?.find(
          c => c.categoryId === item.categoryId && c.available && parseFloat(c.price) > 0
        );
        if (!bc) continue;

        restored.push({
          branchId: branch.id,
          categoryId: bc.categoryId,
          branchName: `${branch.brewery?.name} — ${branch.address}`,
          categoryName: bc.category?.name ?? `Categoría ${bc.categoryId}`,
          price: parseFloat(bc.price),
        });
      }

      if (restored.length > 0) {
        this.selections = restored;
        this.branchesForMap = restored.map(s => s.branchId);
      } else {
        // Saved data no longer valid, clear it
        localStorage.removeItem(this.STORAGE_KEY);
      }
    } catch (e) {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  // ── Total y validación ────────────────────────────────────────────

  calculateTotal(): number {
    return this.selections.reduce((sum, s) => sum + s.price, 0);
  }

  hasMinimumBreweries(): boolean {
    return this.selections.length >= this.minBreweries;
  }

  // ── Agregar al carrito ────────────────────────────────────────────

  addToCart(): void {
    if (this.selections.length < this.minBreweries) {
      this.openErrorModal(
        `Debes seleccionar al menos ${this.minBreweries} paradas para crear tu recorrido`,
        'error', 'Selección insuficiente'
      );
      return;
    }

    const cartSelections: ReservationSelectionCreate[] = this.selections.map(s => ({
      branchId: s.branchId,
      categoryId: s.categoryId,
      branchName: s.branchName
    }));

    const pricePerUnit = this.calculateTotal();
    this.cartService.addCustomRoute(cartSelections, 1, pricePerUnit);

    this.trackingService.track('AddToCart', {
      content_ids: this.selections.map(s => s.branchId),
      content_name: 'Recorrido Personalizado',
      content_type: 'product',
      value: pricePerUnit,
      currency: 'USD',
      num_items: this.selections.length
    });

    this.cartToastService.show({
      title: 'Recorrido personalizado',
      subtitle: `${this.selections.length} paradas · $${pricePerUnit.toFixed(2)}`
    });

    // Clear saved selections after adding to cart
    this.selections = [];
    this.branchesForMap = [];
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // ── Helpers ───────────────────────────────────────────────────────

  openErrorModal(message: string, type: string, title: string): void {
    this.dialog.open(ResponseModalComponent, {
      data: { title, type, message: message ?? 'Intentalo mas tarde' }
    });
  }
}
